import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { Loader2, ShieldCheck } from 'lucide-react';

interface MfaVerifyProps {
  onSuccess: () => void;
  onCancel?: () => void;
}

export function MfaVerify({ onSuccess, onCancel }: MfaVerifyProps) {
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [verifying, setVerifying] = useState(false);
  const [factorId, setFactorId] = useState<string | null>(null);
  const [factorType, setFactorType] = useState<string | null>(null);
  const [challengeId, setChallengeId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function setup() {
      try {
        const { data } = await supabase.auth.mfa.listFactors();
        if (!data) return;

        // Prefer TOTP over phone
        const verifiedFactors = data.all.filter((f) => f.status === 'verified');
        const totp = verifiedFactors.find((f) => f.factor_type === 'totp');
        const phone = verifiedFactors.find((f) => f.factor_type === 'phone');
        const factor = totp || phone;

        if (!factor) {
          setError('No MFA factor found. Please enroll first.');
          setLoading(false);
          return;
        }

        setFactorId(factor.id);
        setFactorType(factor.factor_type);

        // Create challenge (sends SMS for phone factor)
        const { data: challenge, error: challengeError } = await supabase.auth.mfa.challenge({
          factorId: factor.id,
        });

        if (challengeError) {
          setError(challengeError.message);
        } else if (challenge) {
          setChallengeId(challenge.id);
        }
      } catch (e: any) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    }

    setup();
  }, []);

  const handleVerify = async () => {
    if (!factorId || !challengeId || code.length !== 6) return;

    setVerifying(true);
    setError(null);

    try {
      const { error: verifyError } = await supabase.auth.mfa.verify({
        factorId,
        challengeId,
        code,
      });

      if (verifyError) {
        setError(verifyError.message);
        setCode('');
        setVerifying(false);
        return;
      }

      // Refresh session to reflect AAL2
      await supabase.auth.refreshSession();
      onSuccess();
    } catch (e: any) {
      setError(e.message);
      setVerifying(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-2 p-3 rounded-full bg-primary/10 w-fit">
            <ShieldCheck className="h-8 w-8 text-primary" />
          </div>
          <CardTitle>Two-Factor Authentication</CardTitle>
          <CardDescription>
            {factorType === 'phone'
              ? 'Enter the code sent to your phone'
              : 'Enter the code from your authenticator app'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex justify-center">
            <InputOTP
              maxLength={6}
              value={code}
              onChange={setCode}
              onComplete={handleVerify}
            >
              <InputOTPGroup>
                <InputOTPSlot index={0} />
                <InputOTPSlot index={1} />
                <InputOTPSlot index={2} />
                <InputOTPSlot index={3} />
                <InputOTPSlot index={4} />
                <InputOTPSlot index={5} />
              </InputOTPGroup>
            </InputOTP>
          </div>

          {error && (
            <p className="text-sm text-destructive text-center">{error}</p>
          )}

          <Button
            onClick={handleVerify}
            disabled={code.length !== 6 || verifying}
            className="w-full"
          >
            {verifying ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : null}
            Verify
          </Button>

          {onCancel && (
            <Button variant="ghost" onClick={onCancel} className="w-full">
              Cancel
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
